import { Experimentation } from '@alto/experimentation';
import { type Cart } from '@alto/orders_api/types/v1/cart';
import { type CartItem, type PriceType } from '@alto/orders_api/types/v1/cart_item';
import { type CartEndpointFetchActiveRequest } from '@alto/scriptdash/alto/patient_app/carts/v1/cart_endpoint';
import { useMemo } from 'react';
import { getReduxCart } from '~shared/features/cart/selectors/getReduxCart';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useIsInCheckout } from '~shared/hooks';
import { staleTimes } from '~shared/queries/constants';
import { queries } from '~shared/queries/query-keys';
import { useQuery } from '~shared/react-query';
import getIsAuthorized from '~shared/selectors/auth/getIsAuthorized';
import { useSelectorShared } from '~shared/store';

const params: CartEndpointFetchActiveRequest = {};
type CartData = {
  cart: Cart | null | undefined;
  cartItems: CartItem[];
  cartSize: number;
  // map from prescription id to auto refill status
  cartAutoRefills: Record<number, boolean>;
  isError: boolean;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  error: unknown | undefined;
  isLoading: boolean;
  selectedPaymentTypes: Record<number, PriceType>;
  updatedAt: number;
};

export const useCartData = (): CartData => {
  const { value: serverSideCartEnabled } = Experimentation.useFeatureFlag('server_side_cart');
  const isEditingExistingOrder = useSelectorShared(getIsEditingExistingOrder);
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const isInCheckout = useIsInCheckout();

  // query is disabled only if enabled === false
  // https://alto.slack.com/archives/C017B78JVL0/p1674166049892829
  const queryEnabled: boolean =
    (serverSideCartEnabled && isAuthorized && !isEditingExistingOrder && !isInCheckout) || false;
  const { data, isError, error, isLoading, dataUpdatedAt } = useQuery({
    ...queries.cart.active(params),
    enabled: queryEnabled,
    staleTime: staleTimes.fiveMinutes,
  });
  const reduxCart = useSelectorShared(getReduxCart);
  const cart = data?.data?.data;

  const useRedux = !serverSideCartEnabled || isEditingExistingOrder || isInCheckout;
  const cartItems = useMemo(() => {
    if (useRedux) {
      if (isInCheckout && !isEditingExistingOrder && cart?.items) {
        return reduxCart.items.map((reduxItem) => {
          const clonedItem = { ...reduxItem };
          const serverSideCartItems = cart.items || [];
          const matchingServerItem = serverSideCartItems.find(
            (serverItem) =>
              serverItem.resource_id === reduxItem.resource_id && serverItem.resource_type === reduxItem.resource_type,
          );
          if (matchingServerItem) {
            clonedItem.id = matchingServerItem.id;
          }

          return clonedItem;
        });
      }
      return reduxCart.items;
    }
    return cart?.items || [];
  }, [useRedux, reduxCart.items, cart?.items, isInCheckout, isEditingExistingOrder]);
  const cartAutoRefills = useMemo(() => {
    return buildCartAutoRefillMap(cartItems);
  }, [cartItems]);
  const selectedPaymentTypes = useMemo(() => {
    return buildSelectedPaymentTypes(cartItems);
  }, [cartItems]);

  const cartToUse = useMemo(() => {
    if (useRedux) {
      if (isInCheckout && !isEditingExistingOrder && cart?.id) {
        return {
          ...reduxCart,
          id: cart.id,
        };
      }
      return reduxCart;
    }
    return cart;
  }, [useRedux, reduxCart, cart, isInCheckout, isEditingExistingOrder]);

  if (useRedux) {
    return {
      cart: cartToUse,
      cartItems,
      cartSize: cartItems.length,
      cartAutoRefills,
      isError: false,
      error: undefined,
      isLoading: false,
      selectedPaymentTypes,
      updatedAt: new Date(reduxCart.updated_at).valueOf(),
    };
  }

  return {
    cart,
    cartItems: cart?.items || [],
    cartSize: cart?.items?.length || 0,
    cartAutoRefills,
    isError,
    error,
    isLoading,
    selectedPaymentTypes,
    updatedAt: dataUpdatedAt,
  };
};

const buildCartAutoRefillMap = (cartItems: CartItem[]): Record<number, boolean> => {
  return cartItems.reduce<Record<number, boolean>>((acc, cartItem) => {
    if (cartItem.auto_refill_enabled === null || cartItem.auto_refill_enabled === undefined) {
      return acc;
    }
    acc[cartItem.resource_id] = cartItem.auto_refill_enabled;
    return acc;
  }, {});
};

const buildSelectedPaymentTypes = (cartItems: CartItem[]): Record<number, PriceType> => {
  return cartItems.reduce((res: Record<number, PriceType>, item: CartItem) => {
    if (item.resource_id && item.selected_price_type) {
      res[item.resource_id] = item.selected_price_type;
    }
    return res;
  }, {});
};
